import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit, Save, X, Check, Image as ImageIcon, Palette, Plus, Upload, Layout, FileText, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import AdminPageHeader from '../../components/AdminPageHeader';
import useThemeStore from '../../store/themeStore';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import ShopAdvantagesManager from '../../components/admin/ShopAdvantagesManager';
import NewsletterPopupManager from '../../components/admin/NewsletterPopupManager';
import ContactSettingsManager from '../../components/admin/ContactSettingsManager';
import LegalPagesManager from '../../components/admin/LegalPagesManager';
import BrandingManager from '../../components/admin/BrandingManager';
import ColorPalettesManager from '../../components/admin/ColorPalettesManager';
import LayoutsManager from '../../components/admin/LayoutsManager';
import SavedThemesManager from '../../components/admin/SavedThemesManager';
import PageBuilderManager from '../../components/admin/PageBuilderManager';
import BackgroundSelector from '../../components/BackgroundSelector';
import { formatEUR } from '../../utils/format';

const DesignManager = () => {
  const globalActiveTheme = useThemeStore(state => state.activeTheme);
  const globalBranding = useThemeStore(state => state.branding);
  const globalLayout = useThemeStore(state => state.layout);
  const setGlobalActiveTheme = useThemeStore(state => state.setActiveTheme);
  const setGlobalBranding = useThemeStore(state => state.setBranding);
  const setGlobalLayout = useThemeStore(state => state.setLayout);
  
  // Page Builder State
  const [selectedPage, setSelectedPage] = useState('home');
  const [allProducts, setAllProducts] = useState([]);
  const [selectedFeaturedProducts, setSelectedFeaturedProducts] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Legal Pages State - moved to LegalPagesManager component

  // Bullet Style State
  const [bulletStyle, setBulletStyle] = useState({ style: 'square', color: '#C4A084' });
  const [bulletLoading, setBulletLoading] = useState(false);

  // Theme Builder State
  const [themes, setThemes] = useState([]);
  const [activeTheme, setActiveTheme] = useState(null);
  const [branding, setBranding] = useState(globalBranding);
  const [selectedLayout, setSelectedLayout] = useState(globalLayout);
  const [backgroundSections, setBackgroundSections] = useState({
    global: { id: 'solid-beige', type: 'solid', value: '#E8E0D5', name: 'Beige' },
    hero: null,
    products: null,
    newsletter: null,
    footer: null
  });
  const [newTheme, setNewTheme] = useState({
    name: '',
    colors: {
      primary: '#C4A084',
      secondary: '#B89968',
      tertiary: '#A88B5E',
      background: '#E8E0D5',
      buttonText: '#FFFFFF',
      footerBg: '#C4A084'
    }
  });

  // Page Builder Functions
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/products?active_only=true');
      setAllProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const loadFeaturedProducts = () => {
    const saved = localStorage.getItem('featured-products');
    if (saved) {
      setSelectedFeaturedProducts(JSON.parse(saved));
    }
  };

  const saveFeaturedProducts = () => {
    localStorage.setItem('featured-products', JSON.stringify(selectedFeaturedProducts));
    toast.success('Featured Products gespeichert!');
    setShowProductSelector(false);
  };

  const toggleProductSelection = (productId) => {
    if (selectedFeaturedProducts.includes(productId)) {
      setSelectedFeaturedProducts(selectedFeaturedProducts.filter(id => id !== productId));
    } else {
      if (selectedFeaturedProducts.length >= 12) {
        toast.error('Maximal 12 Featured Products möglich');
        return;
      }
      setSelectedFeaturedProducts([...selectedFeaturedProducts, productId]);
    }
  };

  // Theme Builder Functions
  const loadBulletStyle = async () => {
    try {
      const response = await axiosInstance.get('/settings/bullet-style');
      setBulletStyle(response.data);
    } catch (error) {
      console.error('Error loading bullet style:', error);
    }
  };

  const saveBulletStyle = async (style, color) => {
    setBulletLoading(true);
    try {
      await axiosInstance.put('/settings/bullet-style', { style, color });
      setBulletStyle({ style, color });
      toast.success('Aufzählungszeichen gespeichert!');
    } catch (error) {
      console.error('Error saving bullet style:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setBulletLoading(false);
    }
  };

  const loadThemes = () => {
    try {
      const savedThemes = localStorage.getItem('color-themes');
      if (savedThemes) {
        const parsed = JSON.parse(savedThemes);
        setThemes(parsed);
        const active = parsed.find(t => t.isActive);
        if (active) setActiveTheme(active);
      } else {
        const defaultThemes = [
          {
            id: 'default-goldbraun',
            name: 'Goldbraun Elegant (Standard)',
            isActive: true,
            colors: {
              primary: '#C4A084',
              secondary: '#B89968',
              tertiary: '#A88B5E',
              background: '#E8E0D5',
              buttonText: '#FFFFFF',
              footerBg: '#C4A084'
            },
            description: 'Warme goldbraun-beige Töne für ein edles Erscheinungsbild'
          },
          {
            id: 'pastel-rosa',
            name: 'Pastell Rosa',
            isActive: false,
            colors: {
              primary: '#FFB6C1',
              secondary: '#FF69B4',
              tertiary: '#FFC0CB',
              background: '#FFF0F5',
              buttonText: '#8B7355',
              footerBg: '#FFE4E1'
            },
            description: 'Sanfte rosa Pastelltöne für feminine Eleganz'
          },
          {
            id: 'pastel-gruen',
            name: 'Pastell Grün',
            isActive: false,
            colors: {
              primary: '#98D8C8',
              secondary: '#5F9EA0',
              tertiary: '#90EE90',
              background: '#F0FFF4',
              buttonText: '#2F4F4F',
              footerBg: '#E0F2F1'
            },
            description: 'Frische grüne Pastelltöne für natürliche Ruhe'
          },
          {
            id: 'pastel-blau',
            name: 'Pastell Blau',
            isActive: false,
            colors: {
              primary: '#B0E0E6',
              secondary: '#4682B4',
              tertiary: '#87CEEB',
              background: '#F0F8FF',
              buttonText: '#1E3A5F',
              footerBg: '#E6F3FF'
            },
            description: 'Beruhigende blaue Pastelltöne für Vertrauen'
          },
          {
            id: 'pastel-gelb',
            name: 'Pastell Gelb',
            isActive: false,
            colors: {
              primary: '#F0E68C',
              secondary: '#DAA520',
              tertiary: '#FFFFE0',
              background: '#FFFACD',
              buttonText: '#8B7355',
              footerBg: '#FFF9E6'
            },
            description: 'Sonnige gelbe Pastelltöne für positive Energie'
          }
        ];
        setThemes(defaultThemes);
        setActiveTheme(defaultThemes[0]);
        localStorage.setItem('color-themes', JSON.stringify(defaultThemes));
      }
    } catch (error) {
      console.error('Error loading themes:', error);
    }
  };

  const loadBranding = () => {
    const saved = localStorage.getItem('shop-branding');
    if (saved) {
      setBranding(JSON.parse(saved));
    }
  };

  const loadLayout = () => {
    const saved = localStorage.getItem('page-layout');
    if (saved) {
      setSelectedLayout(saved);
    }
  };

  const saveBranding = () => {
    localStorage.setItem('shop-branding', JSON.stringify(branding));
    setGlobalBranding(branding);
    toast.success('Branding gespeichert und angewendet!');
  };

  const saveLayout = (layoutId) => {
    setSelectedLayout(layoutId);
    localStorage.setItem('page-layout', layoutId);
    setGlobalLayout(layoutId);
    toast.success('Layout gespeichert und angewendet!');
  };

  const saveNewTheme = () => {
    if (!newTheme.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    const theme = {
      id: `theme-${Date.now()}`,
      name: newTheme.name,
      colors: newTheme.colors,
      isActive: false,
      createdAt: new Date().toISOString()
    };

    const updatedThemes = [...themes, theme];
    setThemes(updatedThemes);
    localStorage.setItem('color-themes', JSON.stringify(updatedThemes));
    
    toast.success(`Farbpalette "${theme.name}" gespeichert!`);
    setNewTheme({
      name: '',
      colors: {
        primary: '#C4A084',
        secondary: '#B89968',
        tertiary: '#A88B5E',
        background: '#E8E0D5',
        buttonText: '#FFFFFF',
        footerBg: '#C4A084'
      }
    });
  };

  const activateTheme = (themeId) => {
    const updatedThemes = themes.map(t => ({
      ...t,
      isActive: t.id === themeId
    }));
    setThemes(updatedThemes);
    localStorage.setItem('color-themes', JSON.stringify(updatedThemes));
    
    const activated = updatedThemes.find(t => t.id === themeId);
    setActiveTheme(activated);
    setGlobalActiveTheme(activated);
    
    toast.success(`Theme "${activated.name}" aktiviert!`);
  };

  const deleteTheme = (themeId) => {
    if (!window.confirm('Möchten Sie diese Farbpalette wirklich löschen?')) return;
    
    const updatedThemes = themes.filter(t => t.id !== themeId);
    setThemes(updatedThemes);
    localStorage.setItem('color-themes', JSON.stringify(updatedThemes));
    
    toast.success('Farbpalette gelöscht');
  };

  const loadBackgroundSections = () => {
    const saved = localStorage.getItem('background-sections');
    if (saved) {
      setBackgroundSections(JSON.parse(saved));
    }
  };
  
  const saveBackgroundSections = () => {
    localStorage.setItem('background-sections', JSON.stringify(backgroundSections));
    toast.success('Hintergründe gespeichert und angewendet!');
    
    // Trigger page reload to apply backgrounds
    setTimeout(() => window.location.reload(), 1000);
  };

  useEffect(() => {
    // Load initial data on mount - this is a valid pattern for data fetching
    fetchProducts();
    loadFeaturedProducts();
    loadThemes();
    loadBranding();
    loadLayout();
    loadBulletStyle();
    // fetchLegalPages moved to component
    loadBackgroundSections();
  }, []);

  // Generate SVG preview (mobile-friendly, no Canvas)
  const generatePreview = (pageType) => {
    try {
      const bgColor = activeTheme?.colors?.background || '#E8E0D5';
      const primaryColor = activeTheme?.colors?.primary || '#C4A084';
      const shopName = branding?.shopName || 'Ihr Shop';
      const pageLabel = pageType || 'Vorschau';
      
      const svg = `
        <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style="stop-color:${bgColor};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${primaryColor};stop-opacity:1" />
            </linearGradient>
          </defs>
          <rect width="400" height="300" fill="url(#grad)" />
          <text x="200" y="150" text-anchor="middle" font-family="serif" font-size="32" font-weight="bold" fill="white">${shopName}</text>
          <text x="200" y="200" text-anchor="middle" font-family="sans-serif" font-size="16" fill="white">${pageLabel}</text>
        </svg>
      `;
      
      return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
    } catch (error) {
      console.error('Error generating preview:', error);
      // Return a simple fallback SVG
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI0U4RTBENSIvPjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0ic2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM2RDVBNDciPlZvcnNjaGF1PC90ZXh0Pjwvc3ZnPg==';
    }
  };

  const pages = React.useMemo(() => [
    {
      id: 'home',
      name: 'Startseite',
      description: 'Ihre Hauptseite - Das sehen Kunden als Erstes',
      preview: null,
      icon: '🏠',
      sections: [
        { 
          id: 'hero', 
          name: 'Kopfbereich mit Willkommenstext', 
          description: 'Der große Bereich ganz oben mit Ihrem Haupttext',
          editable: true, 
          link: '/admin/design',
          tab: 'branding'
        },
        { 
          id: 'features', 
          name: 'Ihre Shop-Vorteile', 
          description: 'Zeigen Sie die Vorteile Ihres Shops (z.B. "Kostenloser Versand")',
          editable: true,
          custom: true,
          onClick: () => document.querySelector('[value="advantages"]')?.click()
        },
        { 
          id: 'categories', 
          name: 'Produktkategorien', 
          description: 'Die Kacheln mit Ihren Produkt-Gruppen',
          editable: true, 
          link: '/admin/categories' 
        },
        { 
          id: 'featured-products', 
          name: 'Empfohlene Produkte', 
          description: 'Wählen Sie bis zu 12 Produkte für die Startseite aus',
          editable: true, 
          custom: true 
        }
      ]
    },
    {
      id: 'products',
      name: 'Alle Produkte',
      description: 'Die Seite mit Ihrer kompletten Produktliste',
      preview: null,
      icon: '🛍️',
      sections: [
        { 
          id: 'products-list', 
          name: 'Ihre Produkte verwalten', 
          description: 'Produkte hinzufügen, bearbeiten oder löschen',
          editable: true, 
          link: '/admin/products' 
        },
        { 
          id: 'filters', 
          name: 'Filter & Kategorien', 
          description: 'Kategorien zum Filtern der Produkte',
          editable: true, 
          link: '/admin/categories' 
        }
      ]
    },
    {
      id: 'categories',
      name: 'Kategorien-Übersicht',
      description: 'Zeigt alle Ihre Produktkategorien auf einen Blick',
      preview: null,
      icon: '📂',
      sections: [
        { 
          id: 'category-grid', 
          name: 'Kategorien verwalten', 
          description: 'Kategorien hinzufügen oder bearbeiten',
          editable: true, 
          link: '/admin/categories' 
        }
      ]
    }
  ], [allProducts, branding]);

  const presetThemes = [
    {
      name: 'Goldbraun Elegant',
      colors: {
        primary: '#C4A084',
        secondary: '#B89968',
        tertiary: '#A88B5E',
        background: '#E8E0D5',
        buttonText: '#FFFFFF',
        footerBg: '#C4A084'
      }
    },
    {
      name: 'Pastell Rosa',
      colors: {
        primary: '#FFB6C1',
        secondary: '#FF69B4',
        tertiary: '#FFC0CB',
        background: '#FFF0F5',
        buttonText: '#8B7355',
        footerBg: '#FFE4E1'
      }
    },
    {
      name: 'Pastell Grün',
      colors: {
        primary: '#98D8C8',
        secondary: '#5F9EA0',
        tertiary: '#90EE90',
        background: '#F0FFF4',
        buttonText: '#2F4F4F',
        footerBg: '#E0F2F1'
      }
    },
    {
      name: 'Pastell Blau',
      colors: {
        primary: '#B0E0E6',
        secondary: '#4682B4',
        tertiary: '#87CEEB',
        background: '#F0F8FF',
        buttonText: '#1E3A5F',
        footerBg: '#E6F3FF'
      }
    },
    {
      name: 'Pastell Gelb',
      colors: {
        primary: '#F0E68C',
        secondary: '#DAA520',
        tertiary: '#FFFFE0',
        background: '#FFFACD',
        buttonText: '#8B7355',
        footerBg: '#FFF9E6'
      }
    },
    {
      name: 'Klassisch Blau',
      colors: {
        primary: '#4A90E2',
        secondary: '#357ABD',
        tertiary: '#2E6DA4',
        background: '#F5F7FA',
        buttonText: '#FFFFFF',
        footerBg: '#4A90E2'
      }
    },
    {
      name: 'Natürlich Grün',
      colors: {
        primary: '#7CB342',
        secondary: '#689F38',
        tertiary: '#558B2F',
        background: '#F1F8E9',
        buttonText: '#FFFFFF',
        footerBg: '#7CB342'
      }
    },
    {
      name: 'Rosa Elegant',
      colors: {
        primary: '#E91E63',
        secondary: '#C2185B',
        tertiary: '#AD1457',
        background: '#FCE4EC',
        buttonText: '#FFFFFF',
        footerBg: '#E91E63'
      }
    }
  ];

  const pageLayouts = [
    {
      id: 'default',
      name: 'Standard Layout',
      description: 'Kategorien links, Inhalte rechts',
      preview: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI0U4RTBENSIvPjxyZWN0IHg9IjEwIiB5PSIxMCIgd2lkdGg9IjEwMCIgaGVpZ2h0PSIyODAiIGZpbGw9IiNDNEEwODQiIG9wYWNpdHk9IjAuMyIvPjxyZWN0IHg9IjEyMCIgeT0iMTAiIHdpZHRoPSIyNzAiIGhlaWdodD0iMjgwIiBmaWxsPSIjQzRBMDg0IiBvcGFjaXR5PSIwLjIiLz48dGV4dCB4PSIyMDAiIHk9IjE1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE4IiBmaWxsPSIjNkQ1QTQ3Ij5TdGFuZGFyZCBMYXlvdXQ8L3RleHQ+PHRleHQgeD0iNjAiIHk9IjE1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjNkQ1QTQ3Ij5NZW7DvDwvdGV4dD48L3N2Zz4='
    },
    {
      id: 'wide',
      name: 'Breites Layout',
      description: 'Kategorien oben, Produkte in voller Breite',
      preview: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI0U4RTBENSIvPjxyZWN0IHg9IjEwIiB5PSIxMCIgd2lkdGg9IjM4MCIgaGVpZ2h0PSI1MCIgZmlsbD0iI0I4OTk2OCIgb3BhY2l0eT0iMC4zIi8+PHJlY3QgeD0iMTAiIHk9IjcwIiB3aWR0aD0iMzgwIiBoZWlnaHQ9IjIyMCIgZmlsbD0iI0I4OTk2OCIgb3BhY2l0eT0iMC4yIi8+PHRleHQgeD0iMjAwIiB5PSIxODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzZENUE0NyI+QnJlaXRlcyBMYXlvdXQ8L3RleHQ+PHRleHQgeD0iMjAwIiB5PSI0MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjNkQ1QTQ3Ij5LYXRlZ29yaWVuPC90ZXh0Pjwvc3ZnPg=='
    },
    {
      id: 'grid',
      name: 'Grid Layout',
      description: 'Große Produktbilder im Raster',
      preview: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI0U4RTBENSIvPjxyZWN0IHg9IjEwIiB5PSIxMCIgd2lkdGg9IjExNSIgaGVpZ2h0PSIxMzUiIGZpbGw9IiNBODhCNUUiIG9wYWNpdHk9IjAuMyIvPjxyZWN0IHg9IjE0MCIgeT0iMTAiIHdpZHRoPSIxMTUiIGhlaWdodD0iMTM1IiBmaWxsPSIjQTg4QjVFIiBvcGFjaXR5PSIwLjMiLz48cmVjdCB4PSIyNzAiIHk9IjEwIiB3aWR0aD0iMTE1IiBoZWlnaHQ9IjEzNSIgZmlsbD0iI0E4OEI1RSIgb3BhY2l0eT0iMC4zIi8+PHJlY3QgeD0iMTAiIHk9IjE1NSIgd2lkdGg9IjExNSIgaGVpZ2h0PSIxMzUiIGZpbGw9IiNBODhCNUUiIG9wYWNpdHk9IjAuMyIvPjxyZWN0IHg9IjE0MCIgeT0iMTU1IiB3aWR0aD0iMTE1IiBoZWlnaHQ9IjEzNSIgZmlsbD0iI0E4OEI1RSIgb3BhY2l0eT0iMC4zIi8+PHJlY3QgeD0iMjcwIiB5PSIxNTUiIHdpZHRoPSIxMTUiIGhlaWdodD0iMTM1IiBmaWxsPSIjQTg4QjVFIiBvcGFjaXR5PSIwLjMiLz48dGV4dCB4PSIyMDAiIHk9IjE1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE4IiBmaWxsPSIjNkQ1QTQ3Ij5HcmlkIExheW91dDwvdGV4dD48L3N2Zz4='
    },
    {
      id: 'minimal',
      name: 'Minimalistisch',
      description: 'Reduziertes Design, viel Weißraum',
      preview: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI0U4RTBENSIvPjxyZWN0IHg9IjUwIiB5PSI1MCIgd2lkdGg9IjMwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiM2RDVBNDciIG9wYWNpdHk9IjAuMSIvPjxyZWN0IHg9IjcwIiB5PSI4MCIgd2lkdGg9IjI2MCIgaGVpZ2h0PSI0MCIgZmlsbD0iIzZENUE0NyIgb3BhY2l0eT0iMC4yIi8+PHJlY3QgeD0iNzAiIHk9IjE0MCIgd2lkdGg9IjI2MCIgaGVpZ2h0PSI0MCIgZmlsbD0iIzZENUE0NyIgb3BhY2l0eT0iMC4yIi8+PHRleHQgeD0iMjAwIiB5PSIxNTAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzZENUE0NyI+TWluaW1hbCBMYXlvdXQ8L3RleHQ+PC9zdmc+'
    }
  ];

  const loadPreset = (preset) => {
    setNewTheme(prev => ({
      name: prev.name || preset.name,
      colors: preset.colors
    }));
    toast.success(`Farben von "${preset.name}" geladen`);
  };

  const standardColors = [
    { name: 'Rot', color: '#DC2626' },
    { name: 'Rosa', color: '#EC4899' },
    { name: 'Orange', color: '#F97316' },
    { name: 'Gelb', color: '#FBBF24' },
    { name: 'Grün', color: '#10B981' },
    { name: 'Hellgrün', color: '#84CC16' },
    { name: 'Türkis', color: '#14B8A6' },
    { name: 'Cyan', color: '#06B6D4' },
    { name: 'Blau', color: '#3B82F6' },
    { name: 'Hellblau', color: '#60A5FA' },
    { name: 'Indigo', color: '#6366F1' },
    { name: 'Lila', color: '#8B5CF6' },
    { name: 'Goldbraun', color: '#C4A084' },
    { name: 'Braun', color: '#92400E' },
    { name: 'Hellbraun', color: '#D2B48C' },
    { name: 'Beige', color: '#F5F5DC' },
    { name: 'Hellbeige', color: '#E8E0D5' },
    { name: 'Grau', color: '#6B7280' },
    { name: 'Dunkelgrau', color: '#374151' },
    { name: 'Schwarz', color: '#1F2937' },
    { name: 'Weiß', color: '#FFFFFF' },
    { name: 'Creme', color: '#FDF4E3' },
    { name: 'Ivory', color: '#FFFFF0' },
    { name: 'Sand', color: '#F4E4C1' }
  ];

  // Bullet Style Options
  const bulletStyles = [
    { id: 'square', name: 'Quadrat', symbol: '▪' },
    { id: 'square-outline', name: 'Quadrat (Rahmen)', symbol: '▫' },
    { id: 'diamond', name: 'Raute', symbol: '◆' },
    { id: 'diamond-outline', name: 'Raute (Rahmen)', symbol: '◇' },
    { id: 'circle', name: 'Kreis', symbol: '●' },
    { id: 'circle-outline', name: 'Kreis (Rahmen)', symbol: '○' },
    { id: 'check', name: 'Häkchen', symbol: '✓' },
    { id: 'arrow', name: 'Pfeil', symbol: '→' },
    { id: 'star', name: 'Stern', symbol: '★' },
    { id: 'heart', name: 'Herz', symbol: '♥' },
  ];

  const bulletColors = [
    { name: 'Goldbraun', color: '#C4A084' },
    { name: 'Braun', color: '#8B7355' },
    { name: 'Dunkelbraun', color: '#5D4E3C' },
    { name: 'Schwarz', color: '#333333' },
    { name: 'Grau', color: '#6B7280' },
    { name: 'Grün', color: '#10B981' },
    { name: 'Blau', color: '#3B82F6' },
    { name: 'Rosa', color: '#EC4899' },
    { name: 'Rot', color: '#DC2626' },
    { name: 'Orange', color: '#F97316' },
  ];


  // Legal Pages Functions
  // fetchLegalPages moved to LegalPagesManager component

  // Legal Pages functions moved to LegalPagesManager component


  const currentPage = pages.find(p => p.id === selectedPage);
  const featuredProductsData = allProducts.filter(p => selectedFeaturedProducts.includes(p.id));

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-7xl mx-auto">
        <AdminPageHeader 
          title="Seiten & Design" 
          subtitle="Verwalten Sie Ihre Website-Seiten, Farben und Layout"
          icon={Palette}
        />

        <Tabs defaultValue="pages" className="w-full">
          <TabsList className="flex flex-wrap w-full mb-6 bg-[#C4B5A4] p-1 rounded-none border-0 gap-1">
            <TabsTrigger value="pages" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Seiten</TabsTrigger>
            <TabsTrigger value="advantages" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Vorteile</TabsTrigger>
            <TabsTrigger value="newsletter" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Newsletter</TabsTrigger>
            <TabsTrigger value="contact" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Kontakt</TabsTrigger>
            <TabsTrigger value="legal" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Recht</TabsTrigger>
            <TabsTrigger value="branding" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Branding</TabsTrigger>
            <TabsTrigger value="colors" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Farben</TabsTrigger>
            <TabsTrigger value="bullets" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Listen</TabsTrigger>
            <TabsTrigger value="backgrounds" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">BG</TabsTrigger>
            <TabsTrigger value="layouts" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Layouts</TabsTrigger>
            <TabsTrigger value="saved" className="text-xs rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-2 py-1.5">Vorlagen</TabsTrigger>
          </TabsList>

          {/* Seiten Tab */}
          <TabsContent value="pages">
            <PageBuilderManager 
              pages={pages}
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
              currentPage={currentPage}
              branding={branding}
              activeTheme={activeTheme}
              selectedFeaturedProducts={selectedFeaturedProducts}
              featuredProductsData={featuredProductsData}
              onOpenProductSelector={() => setShowProductSelector(true)}
            />
          </TabsContent>

          {/* Shop Advantages Tab */}
          <TabsContent value="advantages">
            <ShopAdvantagesManager />
          </TabsContent>

          {/* Newsletter Popup Tab */}
          <TabsContent value="newsletter">
            <NewsletterPopupManager />
          </TabsContent>

          {/* Contact Settings Tab */}
          <TabsContent value="contact">
            <ContactSettingsManager />
          </TabsContent>

          {/* Legal Pages Tab */}
          <TabsContent value="legal">
            <LegalPagesManager />
          </TabsContent>


          {/* Branding Tab */}
          <TabsContent value="branding">
            <BrandingManager 
              branding={branding}
              setBranding={setBranding}
              onSave={saveBranding}
            />
          </TabsContent>

          <TabsContent value="colors">
            <ColorPalettesManager 
              newTheme={newTheme}
              setNewTheme={setNewTheme}
              onSave={saveNewTheme}
            />
          </TabsContent>

          {/* Bullet Style Tab */}
          <TabsContent value="bullets">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Aufzählungszeichen für Produktbeschreibungen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Preview */}
                <div className="p-4 bg-gray-50 rounded-none border">
                  <p className="text-sm font-medium text-gray-700 mb-3">Vorschau:</p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 text-gray-600">
                      <span style={{ color: bulletStyle.color }} className="mt-0.5">
                        {bulletStyles.find(b => b.id === bulletStyle.style)?.symbol || '▪'}
                      </span>
                      <span>verschiedene Farben</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <span style={{ color: bulletStyle.color }} className="mt-0.5">
                        {bulletStyles.find(b => b.id === bulletStyle.style)?.symbol || '▪'}
                      </span>
                      <span>hochwertige Materialien</span>
                    </div>
                    <div className="flex items-start gap-2 text-gray-600">
                      <span style={{ color: bulletStyle.color }} className="mt-0.5">
                        {bulletStyles.find(b => b.id === bulletStyle.style)?.symbol || '▪'}
                      </span>
                      <span>handgemacht mit Liebe</span>
                    </div>
                  </div>
                </div>

                {/* Style Selection */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Form wählen:</Label>
                  <div className="grid grid-cols-5 gap-2">
                    {bulletStyles.map((style) => (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => saveBulletStyle(style.id, bulletStyle.color)}
                        disabled={bulletLoading}
                        className={`p-3 border-2 rounded-none flex flex-col items-center justify-center transition-all ${
                          bulletStyle.style === style.id 
                            ? 'border-[#C4A084] bg-[#F5EDE4]' 
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <span className="text-2xl" style={{ color: bulletStyle.color }}>{style.symbol}</span>
                        <span className="text-xs mt-1 text-gray-600">{style.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Selection */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Farbe wählen:</Label>
                  <div className="flex flex-wrap gap-2">
                    {bulletColors.map((color) => (
                      <button
                        key={color.color}
                        type="button"
                        onClick={() => saveBulletStyle(bulletStyle.style, color.color)}
                        disabled={bulletLoading}
                        className={`w-10 h-10 rounded-none border-2 transition-all ${
                          bulletStyle.color === color.color 
                            ? 'border-[#5D4E3C] ring-2 ring-[#C4A084]' 
                            : 'border-gray-300 hover:border-gray-500'
                        }`}
                        style={{ backgroundColor: color.color }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  Tipp: Beginnen Sie Zeilen in der Produktbeschreibung mit "-" oder "•" um automatisch Aufzählungszeichen zu erstellen.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* OLD Colors Tab - REMOVE THIS SECTION */}
          <TabsContent value="colors-old" style={{display: 'none'}}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {/* Form */}
              <Card>
                <CardHeader>
                  <CardTitle>Neue Farbpalette erstellen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="theme-name">Name der Vorlage</Label>
                    <Input
                      id="theme-name"
                      value={newTheme.name}
                      onChange={(e) => setNewTheme({ ...newTheme, name: e.target.value })}
                      placeholder="z.B. Meine Farbpalette"
                    />
                  </div>

                  {/* Standard Color Palette */}
                  <div className="mb-4 p-3 md:p-4 bg-gray-50 rounded-lg">
                    <Label className="mb-2 block text-sm md:text-base font-semibold">Standardfarben (Klicken zum Übernehmen)</Label>
                    <div className="grid grid-cols-6 md:grid-cols-8 gap-2">
                      {standardColors.map((standardColor) => (
                        <button
                          key={standardColor.color}
                          type="button"
                          onClick={() => {
                            const currentField = document.activeElement.closest('[data-color-field]')?.getAttribute('data-color-field') || 'primary';
                            setNewTheme({
                              ...newTheme,
                              colors: { ...newTheme.colors, [currentField]: standardColor.color }
                            });
                            toast.success(`${standardColor.name} ausgewählt`);
                          }}
                          className="w-10 h-10 rounded border-2 border-gray-300 hover:border-gray-600 transition-colors"
                          style={{ backgroundColor: standardColor.color }}
                          title={`${standardColor.name} (${standardColor.color})`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-2">Tipp: Klicken Sie auf ein Farbfeld unten, dann auf eine Standardfarbe</p>
                  </div>

                  <div className="space-y-3">
                    <div data-color-field="primary">
                      <Label htmlFor="primary">Primärfarbe (Header, Buttons)</Label>
                      <div className="flex gap-2">
                        <Input
                          id="primary"
                          type="color"
                          value={newTheme.colors.primary}
                          onChange={(e) => setNewTheme({
                            ...newTheme,
                            colors: { ...newTheme.colors, primary: e.target.value }
                          })}
                          className="w-20 h-10"
                        />
                        <Input
                          type="text"
                          value={newTheme.colors.primary}
                          onChange={(e) => setNewTheme({
                            ...newTheme,
                            colors: { ...newTheme.colors, primary: e.target.value }
                          })}
                          data-color-field="primary"
                        />
                      </div>
                    </div>

                    <div data-color-field="secondary">
                      <Label htmlFor="secondary">Sekundärfarbe (Gradient Mitte)</Label>
                      <div className="flex gap-2">
                        <Input
                          id="secondary"
                          type="color"
                          value={newTheme.colors.secondary}
                          onChange={(e) => setNewTheme({
                            ...newTheme,
                            colors: { ...newTheme.colors, secondary: e.target.value }
                          })}
                          className="w-20 h-10"
                        />
                        <Input
                          type="text"
                          value={newTheme.colors.secondary}
                          onChange={(e) => setNewTheme({
                            ...newTheme,
                            colors: { ...newTheme.colors, secondary: e.target.value }
                          })}
                          data-color-field="secondary"
                        />
                      </div>
                    </div>

                    <div data-color-field="tertiary">
                      <Label htmlFor="tertiary">Tertiärfarbe (Gradient Ende)</Label>
                      <div className="flex gap-2">
                        <Input
                          id="tertiary"
                          type="color"
                          value={newTheme.colors.tertiary}
                          onChange={(e) => setNewTheme({
                            ...newTheme,
                            colors: { ...newTheme.colors, tertiary: e.target.value }
                          })}
                          className="w-20 h-10"
                        />
                        <Input
                          type="text"
                          value={newTheme.colors.tertiary}
                          onChange={(e) => setNewTheme({
                            ...newTheme,
                            colors: { ...newTheme.colors, tertiary: e.target.value }
                          })}
                          data-color-field="tertiary"
                        />
                      </div>
                    </div>

                    <div data-color-field="background">
                      <Label htmlFor="background">Hintergrundfarbe</Label>
                      <div className="flex gap-2">
                        <Input
                          id="background"
                          type="color"
                          value={newTheme.colors.background}
                          onChange={(e) => setNewTheme({
                            ...newTheme,
                            colors: { ...newTheme.colors, background: e.target.value }
                          })}
                          className="w-20 h-10"
                        />
                        <Input
                          type="text"
                          value={newTheme.colors.background}
                          onChange={(e) => setNewTheme({
                            ...newTheme,
                            colors: { ...newTheme.colors, background: e.target.value }
                          })}
                          data-color-field="background"
                        />
                      </div>
                    </div>
                  </div>

                  <Button onClick={saveNewTheme} className="w-full rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">
                    <Plus className="h-4 w-4 mr-2" />
                    Vorlage speichern
                  </Button>
                </CardContent>
              </Card>

              {/* Preview & Presets */}
              <div className="space-y-6">
                {/* Preview */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Palette className="h-5 w-5" />
                      Vorschau
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Header Preview */}
                      <div
                        className="p-6 rounded-lg text-white text-center"
                        style={{
                          background: `linear-gradient(135deg, ${newTheme.colors.primary} 0%, ${newTheme.colors.secondary} 50%, ${newTheme.colors.tertiary} 100%)`
                        }}
                      >
                        <h3 className="text-xl font-bold mb-2">Homeyoursoul</h3>
                        <p className="text-sm">Header Vorschau</p>
                      </div>

                      {/* Background Preview */}
                      <div
                        className="p-6 rounded-lg"
                        style={{ backgroundColor: newTheme.colors.background }}
                      >
                        <p className="text-sm text-gray-700">Hintergrund Vorschau</p>
                      </div>

                      {/* Button Preview */}
                      <button
                        className="w-full px-4 py-2 rounded text-white font-medium"
                        style={{
                          background: `linear-gradient(135deg, ${newTheme.colors.primary} 0%, ${newTheme.colors.secondary} 100%)`
                        }}
                      >
                        Button Vorschau
                      </button>
                    </div>
                  </CardContent>
                </Card>

                {/* Preset Templates */}
                <Card>
                  <CardHeader>
                    <CardTitle>Schnellvorlagen</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {presetThemes.map((preset, index) => (
                        <button
                          key={index}
                          onClick={() => loadPreset(preset)}
                          className="w-full p-3 border rounded hover:bg-gray-50 text-left flex items-center gap-3"
                        >
                          <div className="flex gap-1">
                            <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.colors.primary }} />
                            <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.colors.secondary }} />
                            <div className="w-6 h-6 rounded" style={{ backgroundColor: preset.colors.tertiary }} />
                          </div>
                          <span className="text-sm font-medium">{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Backgrounds Tab */}
          <TabsContent value="backgrounds">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Hintergründe & Abschnitte
                  </CardTitle>
                  <Button onClick={saveBackgroundSections} className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">
                    <Save className="h-4 w-4 mr-2" />
                    Speichern & Anwenden
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>💡 Tipp:</strong> Wählen Sie unterschiedliche Hintergründe für verschiedene Bereiche Ihrer Website.
                    Die Hintergründe sind unabhängig von der Farbpalette und bieten Ihnen mehr Gestaltungsfreiheit.
                  </p>
                </div>
                
                <BackgroundSelector
                  sections={backgroundSections}
                  onSectionsChange={setBackgroundSections}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Layouts Tab */}
          <TabsContent value="layouts">
            <LayoutsManager 
              selectedLayout={selectedLayout}
              onSave={saveLayout}
            />
          </TabsContent>

          {/* Saved Themes Tab */}
          <TabsContent value="saved">
            <SavedThemesManager 
              themes={themes}
              onActivate={activateTheme}
              onDelete={deleteTheme}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Product Selector Dialog */}
      <Dialog open={showProductSelector} onOpenChange={setShowProductSelector}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Featured Products auswählen</DialogTitle>
            <p className="text-sm text-gray-600">
              Wählen Sie bis zu 12 Produkte für die Homepage (
              {selectedFeaturedProducts.length}/12 ausgewählt)
            </p>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <p className="text-center py-8">Lädt Produkte...</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 p-4">
                {allProducts.map((product) => {
                  const isSelected = selectedFeaturedProducts.includes(product.id);
                  return (
                    <button
                      key={product.id}
                      onClick={() => toggleProductSelection(product.id)}
                      className={`relative border-2 rounded-lg p-3 transition-all text-left ${
                        isSelected
                          ? 'border-[#C4A084] bg-[#C4A084] bg-opacity-10'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-[#C4A084] text-white rounded-full p-1">
                          <Check className="h-4 w-4" />
                        </div>
                      )}
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-32 object-cover rounded mb-2"
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-200 rounded mb-2 flex items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <h4 className="font-semibold text-sm line-clamp-2">{product.name}</h4>
                      <p className="text-sm text-gray-600">{formatEUR(product.price)}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t pt-4 flex gap-2">
            <Button
              onClick={() => setShowProductSelector(false)}
              variant="outline"
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Abbrechen
            </Button>
            <Button
              onClick={saveFeaturedProducts}
              className="flex-1 bg-[#C4A084] hover:bg-[#B89968]"
            >
              <Save className="h-4 w-4 mr-2" />
              Speichern ({selectedFeaturedProducts.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DesignManager;
